import { RestApi } from './rest.api';

export interface AuthStrategyParams {
  baseUrl: string;
  auth: {
    redirectUri: string
    identityServer: string
    client_id: string,
    urls: {
      authorize: string
      logout: string
      validate: string
      token: string
      syncToken: string
    },
    scope: string
  }
}

export interface HttpParams extends RequestInit {
  url: string
}

interface ProcessSyncCodeParameters {
  code: string;
  http: RestApi;
}

interface SyncTokenResponse {
  access_token: string
  id_token: string
  profile: string
}

export class AuthStorage {
  public static set(key: string, value: string) {
    sessionStorage.setItem(key, value);
  }

  public static get(key: string) {
    return sessionStorage.getItem(key);
  }

  public static remove(key: string) {
    sessionStorage.removeItem(key);
  }
}


export class AuthStrategy {

  private readonly KEY_STORAGE_PROFILE = '_key_openid_profile';
  private readonly KEY_STORAGE_ACCESSTOKEN = '_key_openid_access_token';
  private readonly KEY_STORAGE_IDTOKEN = '_key_openid_id_token_';

  constructor(public config?: Partial<AuthStrategyParams>) { }

  private createNonce() {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < 10; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  queryString(key: string): string {
    const match = new RegExp(`${ key }=([^&]+)`, 'i').exec(location.href);
    return match ? match[1] : ''
  }

  extractSyncCode(): string {
    const code = this.queryString('code');
    if (!code) {
      const match = new RegExp(`/sync/([^&]+)`, 'i').exec(location.href);
      return match ? match[1] : ''
    }
    return code;
  }

  authorize(params?: { state?: string }) {
    var state = encodeURIComponent(params ? params.state : this.createNonce())
    const url = this.config.auth.urls.authorize + '?response_type=' + 'code' // Change this when using OPEN ID
      + '&client_id=' + encodeURIComponent(this.config.auth.client_id)
      + '&response_mode=form_post'
      + '&state=' + state
      + '&redirect_uri=' + this.config.auth.redirectUri
      + '&scope=' + encodeURIComponent(this.config.auth.scope);
    document.cookie = "state="+ state + "; path=/";
    location.href = this.config.baseUrl + url;
  }

  getLogoutUrl(http: RestApi) {
    return http.post(this.getBaseUrl() + this.config.auth.urls.logout, {
      id_token: this.getIdToken(),
      access_token: this.getAccessToken()
    })
  }

  getBaseUrl() {
    const location = window.location;
    const initPathName = '/public/';
    return location.href.split(initPathName)[0].replace(location.hash, '').replace(location.search, '');
  }

  logout(http: RestApi) {
    this.getLogoutUrl(http).subscribe(response => {
      this.removeLocalData();

      if (response.Result.startsWith('http') || response.Result.startsWith('https')) {
        location.href = response.Result;
      }
    });
  }

  processSyncCode({ code, http }: ProcessSyncCodeParameters): Promise<SyncTokenResponse> {
    return new Promise((resolve, reject) => {
      const form = { 'code': code };

      http.post(`api/auth/oauth/syncToken`, form)
        .subscribe((response: SyncTokenResponse) => {
          console.log('Sync data response', response);
          if (response) {
            this.setProfile(response.profile);
            this.setToken({ ...response })
            return resolve(response);
          } else {
            return reject(response);
          }
        }, err => {
          reject(err);
        });
    })
  }

  setProfile(profile: string) {
    AuthStorage.set(this.KEY_STORAGE_PROFILE, JSON.stringify(profile));
  }

  getProfile() {
    return JSON.parse(AuthStorage.get(this.KEY_STORAGE_PROFILE));
  }

  setToken({ id_token, access_token }: { id_token: string, access_token: string }) {
    AuthStorage.set(this.KEY_STORAGE_IDTOKEN, id_token);
    AuthStorage.set(this.KEY_STORAGE_ACCESSTOKEN, access_token)
  }

  getAccessToken() {
    return AuthStorage.get(this.KEY_STORAGE_ACCESSTOKEN);
  }

  getIdToken() {
    return AuthStorage.get(this.KEY_STORAGE_IDTOKEN);
  }

  hasToken() {
    return this.getAccessToken() !== null
  }

  removeLocalData(){
    AuthStorage.remove(this.KEY_STORAGE_ACCESSTOKEN);
    AuthStorage.remove(this.KEY_STORAGE_IDTOKEN);
    AuthStorage.remove(this.KEY_STORAGE_PROFILE);

  }
}
